import useCreateAppliance from '@/api/mutations/appliances/useCreateAppliance';
import useUpdateAppliance from '@/api/mutations/appliances/useUpdateAppliance';
import lineItemKeys from '@/api/queries/lineItems/keyFactory';
import useSearchProductSpecs from '@/api/queries/materials/useSearchProductSpecs';
import { ProductSpecCategory } from '@/gql/graphql';
import SelectItemModal from '@/ui/components/selectItemModal/SelectItemModal';
import AddEditItemSidebar from '@/ui/pages/listedItemsPage/components/AddEditItemSidebar';
import {
  Button,
  Card,
  Combobox,
  SelectOption,
  Switch,
  useToastContext,
  Text,
  H2,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { useDebounce } from '@payaca/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useMemo, useState } from 'react';
import { Prompt, useHistory } from 'react-router';

import LinkedItemRow, { LinkedItemPrice } from './components/LinkedItemRow';
import SpecReadout, { SpecForReadout } from './components/SpecReadout';
import { estimateSettingsPathForCategory } from '@/ui/components/companySettings/estimateSettingsRoutes';
import { ApplianceFromList, NOUN } from './constants';

type Props = {
  category: ProductSpecCategory;
  /** Present in edit mode. */
  appliance?: ApplianceFromList;
};

const ApplianceEditorContent: FC<Props> = ({ category, appliance }) => {
  const history = useHistory();
  const { pushSuccess, pushError } = useToastContext();
  const queryClient = useQueryClient();
  const isEdit = appliance != null;
  const noun = NOUN[category] ?? 'appliance';

  const [selectedSpec, setSelectedSpec] = useState<SpecForReadout | null>(
    appliance ? appliance.productSpec : null
  );
  const [linkedItemIds, setLinkedItemIds] = useState<string[]>(
    appliance?.items.map((item) => item.id) ?? []
  );
  // Per-Item prices, reported by each row's GetLineItem fetch, kept only to
  // total the Items. The rows remain the source of truth for what they show.
  const [itemPrices, setItemPrices] = useState<Record<string, LinkedItemPrice>>(
    {}
  );
  const [isPreferred, setIsPreferred] = useState(
    appliance?.isPreferred ?? false
  );

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useDebounce(() => setDebouncedQuery(query), 300, [query]);

  const [isSelectItemModalOpen, setIsSelectItemModalOpen] = useState(false);
  const [isCreateItemSidebarOpen, setIsCreateItemSidebarOpen] = useState(false);
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(
    null
  );

  const closeEditItemSidebar = () => {
    const id = editingLineItemId;
    setEditingLineItemId(null);
    // The edited Item's row keeps an active GetLineItem observer, so
    // invalidating its query refetches the saved values. The sidebar's save
    // only invalidates the Item lists, not this single-Item query.
    if (id) {
      void queryClient.invalidateQueries({
        queryKey: lineItemKeys.lineItem(id),
      });
    }
  };

  const { data: specResults } = useSearchProductSpecs(
    category,
    debouncedQuery || undefined
  );

  // Disable specs the account already has a live Appliance for — one Appliance
  // per spec, so re-adding would conflict. Edit the existing one from the list.
  const specOptions: SelectOption<string, { alreadyAdded: boolean }>[] =
    useMemo(
      () =>
        specResults?.map((result) => ({
          label: result.productSpec.name,
          value: result.productSpec.id,
          disabled: result.appliance != null,
          metadata: { alreadyAdded: result.appliance != null },
        })) ?? [],
      [specResults]
    );

  // handleSave shows its own error toast, so opt these mutations out of the
  // global mutation error toast to avoid showing two toasts for one failure.
  const { mutateAsync: createAppliance, isPending: isCreating } =
    useCreateAppliance({ meta: { skipGlobalErrorToast: true } });
  const { mutateAsync: updateAppliance, isPending: isUpdating } =
    useUpdateAppliance({ meta: { skipGlobalErrorToast: true } });

  const addLinkedItem = (lineItemId: string) =>
    setLinkedItemIds((ids) =>
      ids.includes(lineItemId) ? ids : [...ids, lineItemId]
    );
  const removeLinkedItem = (lineItemId: string) => {
    setLinkedItemIds((ids) => ids.filter((id) => id !== lineItemId));
    setItemPrices((prev) => {
      if (!(lineItemId in prev)) return prev;
      const { [lineItemId]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const handleItemPriceChange = useCallback(
    (lineItemId: string, price: LinkedItemPrice) =>
      setItemPrices((prev) => {
        const existing = prev[lineItemId];
        if (
          existing &&
          existing.value === price.value &&
          existing.currency?.code === price.currency?.code
        ) {
          return prev;
        }
        return { ...prev, [lineItemId]: price };
      }),
    []
  );

  const prices = linkedItemIds
    .map((id) => itemPrices[id])
    .filter((p): p is LinkedItemPrice => p != null);
  const totalCurrency = prices.find((p) => p.currency)?.currency ?? null;
  const total = prices.reduce((sum, p) => sum + (p.value ?? 0), 0);

  const canSave = selectedSpec != null && linkedItemIds.length > 0;
  const isSaving = isCreating || isUpdating;
  const goBack = () => history.push(estimateSettingsPathForCategory(category));

  // Warn before navigating away with unsaved edits. Compare current state to
  // the values the editor opened with (empty in create mode).
  const initialItemIds = appliance?.items.map((item) => item.id) ?? [];
  const hasUnsavedChanges =
    (selectedSpec?.id ?? null) !== (appliance?.productSpec.id ?? null) ||
    isPreferred !== (appliance?.isPreferred ?? false) ||
    linkedItemIds.length !== initialItemIds.length ||
    initialItemIds.some((id) => !linkedItemIds.includes(id));

  const handleSave = async () => {
    if (!canSave || !selectedSpec) return;
    try {
      if (isEdit) {
        await updateAppliance({
          id: appliance.id,
          itemIds: linkedItemIds,
          isPreferred,
        });
        pushSuccess('Appliance updated');
      } else {
        await createAppliance({
          productSpecId: selectedSpec.id,
          itemIds: linkedItemIds,
          isPreferred,
        });
        pushSuccess('Appliance added');
      }
      goBack();
    } catch {
      pushError('Could not save the appliance. Please try again.');
    }
  };

  let preferredDescription = `Preferred Appliances are matched against the property's heat loss and added to new estimates automatically.`;
  if (selectedSpec?.archivedAt != null) {
    preferredDescription = `This specification is no longer available, so this appliance can't be set as Preferred.`;
  } else if (linkedItemIds.length === 0) {
    preferredDescription = `Add at least one item before this appliance can be set as Preferred.`;
  } else if (category === 'CYLINDER') {
    preferredDescription = `Preferred cylinders are matched to the property's hot water needs and added to new estimates automatically.`;
  } else if (category === 'SOLAR_PANEL') {
    preferredDescription = `Preferred solar panels are matched to the roof design's array size and added to new estimates automatically.`;
  } else if (category === 'INVERTER') {
    preferredDescription = `Preferred inverters are matched to the array's output and added to new estimates automatically.`;
  } else if (category === 'BATTERY') {
    preferredDescription = `Preferred batteries are matched to the system's daily generation and added to new estimates automatically.`;
  }

  return (
    <div>
      <Prompt
        when={hasUnsavedChanges && !isSaving}
        message="There are unsaved changes on the page, are you sure you want to leave?"
      />
      <Card sizeVariant="lg" className="mb-4">
        <Card.Body className="flex flex-col gap-4">
          <div>
            <H2>Appliance specification</H2>
            {!isEdit && (
              <Text>
                Choose the specification from our catalogue by name, brand or
                model.
              </Text>
            )}
          </div>
          {!isEdit && (
            <Combobox
              multiple={false}
              options={specOptions}
              CustomOption={({ option }) => (
                <>
                  <span>{option.label}</span>
                  {option.metadata?.alreadyAdded && (
                    <span className="ml-auto flex shrink-0 items-center pl-2 text-gray-500">
                      Already added
                    </span>
                  )}
                </>
              )}
              value={selectedSpec?.id}
              onChange={(value) => {
                const match = specResults?.find(
                  (r) => r.productSpec.id === value
                );
                // Ignore already-added specs (their option is disabled).
                if (match?.appliance) return;
                setSelectedSpec(match ? match.productSpec : null);
              }}
              query={query}
              setQuery={setQuery}
              filterFunction={null}
              placeholder="Search by name, brand or model"
              inputPlaceholder="Search by name, brand or model"
            />
          )}
          <SpecReadout spec={selectedSpec} />
        </Card.Body>
      </Card>
      {selectedSpec && (
        <>
          <Card sizeVariant="lg" className="mb-4">
            <Card.Body className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <H2>Items</H2>
                  <Text>
                    Each linked item is added to the estimate as its own line.
                  </Text>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="shrink-0 whitespace-nowrap"
                  onClick={() => setIsSelectItemModalOpen(true)}
                >
                  Add item
                </Button>
              </div>
              {linkedItemIds.length > 0 && (
                <div className="flex flex-col">
                  {linkedItemIds.map((lineItemId) => (
                    <LinkedItemRow
                      key={lineItemId}
                      lineItemId={lineItemId}
                      onPriceChange={handleItemPriceChange}
                      onEdit={() => setEditingLineItemId(lineItemId)}
                      onRemove={() => removeLinkedItem(lineItemId)}
                    />
                  ))}
                </div>
              )}
              {linkedItemIds.length > 0 && totalCurrency && (
                <div className="mt-2 flex items-center justify-between pt-1 font-bold">
                  <span>Total customer price (inc. tax)</span>
                  <span>
                    <CurrencyValue value={total} currency={totalCurrency} />
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card sizeVariant="lg" className="mb-4">
            <Card.Body className="flex items-center justify-between gap-5">
              <div className="min-w-0">
                <H2>Mark as preferred</H2>
                <Text>{preferredDescription}</Text>
              </div>
              <div className="shrink-0">
                <Switch
                  value={isPreferred}
                  disabled={
                    linkedItemIds.length === 0 ||
                    selectedSpec.archivedAt != null
                  }
                  onChange={setIsPreferred}
                />
              </div>
            </Card.Body>
          </Card>
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={goBack}>
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          isProcessing={isSaving}
          onClick={() => void handleSave()}
        >
          Save {noun}
        </Button>
      </div>
      <SelectItemModal
        isOpen={isSelectItemModalOpen}
        disabledItemIds={linkedItemIds.map((id) => +id)}
        allowRepeatedItems={false}
        onClose={() => setIsSelectItemModalOpen(false)}
        onSelectItem={(lineItemId) => {
          addLinkedItem(String(lineItemId));
          setIsSelectItemModalOpen(false);
        }}
        onCreateNewItem={() => {
          setIsSelectItemModalOpen(false);
          setIsCreateItemSidebarOpen(true);
        }}
      />
      <AddEditItemSidebar
        isOpen={isCreateItemSidebarOpen}
        onClose={() => setIsCreateItemSidebarOpen(false)}
        onCreateSuccess={(lineItemId) => addLinkedItem(lineItemId)}
        prefill={
          selectedSpec
            ? {
                description: selectedSpec.name,
                image: selectedSpec.image
                  ? {
                      uploadId: selectedSpec.image.id,
                      url: selectedSpec.image.url,
                    }
                  : undefined,
              }
            : undefined
        }
      />
      <AddEditItemSidebar
        isOpen={editingLineItemId != null}
        lineItemId={editingLineItemId ?? undefined}
        onClose={closeEditItemSidebar}
        // Deleting the catalogue Item from here is confusing — use the row's
        // Remove action to unlink it from the Appliance instead.
        canDelete={false}
      />
    </div>
  );
};

export default ApplianceEditorContent;
